import React from "react";

import classnames from "classnames";
import createDecorator from "final-form-focus";
import NetsuiteLogo from "images/logos/NetSuite-logo-text-A-white.png";
import { Field, Form } from "react-final-form";
import { useHistory } from "react-router-dom";
import { useGetApplicationQuery } from "RegisterBenAdmin/api";
import { validateRequired, validateNumber, validateMin, composeValidators, validateMax } from "Utils/Validations";

import LoadingPage from "../components/LoadingPage";
import { paymentURL } from "../urls";
import BodyText, { Body2 } from "Cobalt/BodyText";
import Button from "Cobalt/Button";
import { Card } from "Cobalt/Card";
import { H1, Title, Subtitle } from "Cobalt/Header";
import Footer from "Common/Footer";
import { Grid, Row, Col } from "Common/Grid";
import { Div, Li, Ul } from "Common/HTML";
import Icon from "Common/Icon";
import ThemeInstaller from "Common/ThemeInstaller";

import makeBem from "Utils/makeBem";

const bem = makeBem("RegisterBenAdminLandingPage");
const focusOnErrors = createDecorator();

const isBetween1And9999 = composeValidators(validateRequired, validateNumber, validateMin(1), validateMax(9999));
export const formatEmployeeCount = (value) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }
  const numericValue = parseInt(value, 10);
  // Return null for non-numeric or negative values
  if (Number.isNaN(numericValue) || numericValue < 0) {
    return null;
  }
  return Math.max(1, Math.min(9999, numericValue));
};

const handleKeyDown = (e, input) => {
  if (e.key === "ArrowUp") {
    e.preventDefault(); // Prevent default behavior
    input.onChange((Number(input.value) || 0) + 1); // Increment
  } else if (e.key === "ArrowDown") {
    e.preventDefault(); // Prevent default behavior
    input.onChange((Number(input.value) || 0) - 1); // Decrement
  }
};

// TODO(ben-admin): Add partner logos
const PartnerLogo = () => {
  // const { partner } = usePartnerContext();
  // let src;

  // switch (partner) {
  //   case "netsuite":
  //     src = NetsuiteLogo;
  //     break;
  //   case "primepay":
  //     src = PrimepayLogo;
  //     break;
  //   case "square":
  //     src = SquarePartnerLogo;
  //     break;
  //   default:
  //     src = SimplyInsuredLogo;
  // }
  // const alt = partner === "none" ? "SimplyInsured" : `SimplyInusred + ${partner}`;
  return <img className={bem("Logo")} alt="SimplyInusred + NetSuite" src={NetsuiteLogo} />;
};

const LandingPage = () => {
  const { data: { benAdminLead } = {}, isLoading: isLoadingBenAdminLead } = useGetApplicationQuery();
  const initialEmployeeCount = benAdminLead?.employeeCount || 100;
  const history = useHistory();

  const onSubmitHandler = () => {
    history.push(paymentURL);
  };
  return isLoadingBenAdminLead ? (
    <LoadingPage />
  ) : (
    <>
      <Form onSubmit={onSubmitHandler} decorators={[focusOnErrors]} initialValues={{ employeeCount: initialEmployeeCount }}>
        {({ handleSubmit, values: { employeeCount }, errors: { employeeCount: employeeCountError } }) => (
          <form onSubmit={handleSubmit}>
            <div className="bg-blue-800">
              <Grid>
                <div className={bem("heroRow")}>
                  <PartnerLogo />
                  <H1 className="text-white" mt={3}>
                    Benefits Administration
                  </H1>
                  <Subtitle pt={4} className="text-white">
                    Integrate payroll with benefits for $5 per employee per month
                  </Subtitle>
                  <Subtitle pt={4} className="text-white">
                    <Icon icon="check-circle" flavor="solid" mr={3} />
                    Deductions Synced To NetSuite Payroll Automatically
                  </Subtitle>
                  <Subtitle pt={4} className="text-white">
                    <Icon icon="check-circle" flavor="solid" mr={3} />
                    Online Open Enrollment With No Headaches
                  </Subtitle>
                  <Subtitle pt={4} className="text-white">
                    <Icon icon="check-circle" flavor="solid" mr={3} />
                    Automatic Employee Enrollments And Terminations and one-click renewals
                  </Subtitle>
                  <Subtitle pt={4} className="text-white">
                    <Icon icon="check-circle" flavor="solid" mr={3} />
                    Medical, Dental, Vision, Life, AD&D, Disability and more
                  </Subtitle>
                  <div className="text-center">
                    <Button
                      style={{ width: "300px", marginTop: "60px" }}
                      type="submit"
                      color="primary"
                      size="large"
                      mt={6}
                      {...(employeeCountError ? { disabled: "disabled" } : {})}
                    >
                      Get Started
                    </Button>
                  </div>
                </div>
              </Grid>
            </div>
            <div style={{ backgroundColor: "#fff", padding: "15px 0 20px" }}>
              <div className="flex justify-center items-center">
                <Subtitle className="text-gray" mb={0}>
                  Exclusive Partner
                </Subtitle>
                <img alt="NetSuite logo" className={bem("exclusivePartnerImage")} src={NetsuiteLogo} />
              </div>
            </div>
            <div style={{ padding: "30px 0 50px" }}>
              <div className="mx-auto w-full max-w-screen-lg">
                <Title level={4} mb={5} className="text-center">
                  Pricing
                </Title>
                <div className="flex flex-col sm:flex-row gap-10 justify-center items-center">
                  <div className="text-center md:w-4/12 sm:w-6/12">
                    <BodyText>
                      Number of Employees
                      {benAdminLead?.employeeCount && "*"}
                    </BodyText>
                    <BodyText mt={4}>
                      <Field name="employeeCount" validate={isBetween1And9999} parse={formatEmployeeCount}>
                        {({ input }) => (
                          <input
                            type="text"
                            placeholder="# of employees"
                            className={bem("employeeCountInput")}
                            onKeyDown={(e) => handleKeyDown(e, input)}
                            {...input}
                          />
                        )}
                      </Field>
                      {![null, undefined].includes(benAdminLead?.employeeCount) && (
                        <Body2 mt={4}>
                          *{benAdminLead.employeeCount} employees synced from {benAdminLead.companyName || "your company"}'s payroll
                        </Body2>
                      )}
                      <BodyText color="error" mt={4}>
                        {employeeCountError}&nbsp;
                      </BodyText>
                    </BodyText>
                  </div>
                  <Card className="text-center md:w-4/12 sm:w-6/12" mb={0}>
                    <Title>
                      Annual Cost:
                      <br />
                      <span className="text-4xl">${employeeCount * 5}/month</span>
                    </Title>
                    <BodyText>
                      Monthly Cost: <span className="line-through">${employeeCount * 6}/month</span>
                    </BodyText>
                    <Button type="submit" color="primary" size="large" mt={5} {...(employeeCountError ? { disabled: "disabled" } : {})}>
                      Get Started
                    </Button>
                  </Card>
                </div>
              </div>
            </div>
            <div className="bg-blue" style={{ padding: "30px 0 50px" }}>
              <div className="mx-auto w-full max-w-screen-lg">
                <Title level={4} mb={5} className="text-center text-white">
                  We Simplify It For You!
                </Title>
                <div className="flex flex-col md:flex-row gap-4 mb-4">
                  <div className="md:w-3/12">
                    <div className={classnames("bg-white border-b-blue-200", bem("propValueBox"))}>
                      <Title className={classnames("text-blue", bem("propValueTitle"))}>Payroll Integration</Title>
                      <BodyText mt={2}>Payroll changes automatically sent to your broker and deductions automatically updated.</BodyText>
                    </div>
                  </div>
                  <div className="md:w-3/12">
                    <div className={classnames("bg-white border-b-blue-200", bem("propValueBox"))}>
                      <Title className={classnames("text-blue", bem("propValueTitle"))}>One-Click Open Enrollment</Title>
                      <BodyText mt={2}>Automatic renewals and easy online open enrollment for employees.</BodyText>
                    </div>
                  </div>
                  <div className="md:w-3/12">
                    <div className={classnames("bg-white border-b-blue-200", bem("propValueBox"))}>
                      <Title className={classnames("text-blue", bem("propValueTitle"))}>Employee Onboarding</Title>
                      <BodyText mt={2}>Onboarding completed in minutes, entirely online, with new hire benefits all in one place</BodyText>
                    </div>
                  </div>
                  <div className="md:w-3/12">
                    <div className={classnames("bg-white border-b-blue-200", bem("propValueBox"))}>
                      <Title className={classnames("text-blue", bem("propValueTitle"))}>Benefits Dashboards</Title>
                      <BodyText mt={2}>
                        Manage benefits and estimate costs for new hires. Employee tools to get ID Cards, file claims, and manage dependents.
                      </BodyText>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white" style={{ padding: "50px 0" }}>
              <Grid>
                <Row className="items-center">
                  <Col sm={4}>
                    <Title level={4} mb={5}>
                      How It Works
                    </Title>
                    <Title level={6} className="font-normal">
                      <Ul pl={4}>
                        <Li mt={3}>Invite your existing broker to manage your benefits online</Li>
                        <Li mt={3}>Integrate all your existing benefits into NetSuite payroll</Li>
                        <Li mt={3}>Manage your benefits and automate deductions to NetSuite</Li>
                      </Ul>
                    </Title>
                  </Col>
                  <Col sm={8}>
                    <div style={{ padding: "56.25% 0 0 0", position: "relative" }}>
                      <iframe
                        src="https://player.vimeo.com/video/1030831208?h=112a0214d2&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                        style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
                        title="SimplyInsured NetSuite integration with deductions"
                      />
                    </div>
                    <script src="https://player.vimeo.com/api/player.js" />
                  </Col>
                </Row>
              </Grid>
            </div>
            <div style={{ padding: "30px 0 50px" }}>
              <div className="mx-auto w-full max-w-screen-lg">
                <Title level={4} mb={5} className="text-center">
                  Why Customers Love SimplyInsured!
                </Title>
                <div className="flex flex-col md:flex-row gap-4 mb-4">
                  <Card className="flex flex-col md:w-4/12" mb={0}>
                    <Div className={classnames("border-blue-200", bem("testimonialImage"))}>
                      <img
                        style={{ maxHeight: "100%", marginLeft: "50%", transform: "translateX(-53%)" }}
                        alt="Nik from R&MConstruction Company smiling"
                        src="https://21756504.fs1.hubspotusercontent-na1.net/hubfs/21756504/SI%20-%20Photography%20and%20Graphics/Testimonial%20Images/quote_1_img_d.png"
                      />
                    </Div>
                    <BodyText mb={3}>
                      "SimplyInsured made buying insurance a very simple process but more importantly, I received amazing customer support. Where insurance
                      carriers were slow, SimplyInsured was prompt and personal in their responses."
                    </BodyText>
                    <BodyText style={{ marginTop: "auto" }}>Nik,</BodyText>
                    <BodyText>R&M Construction Company</BodyText>
                  </Card>
                  <Card className="flex flex-col md:w-4/12" mb={0}>
                    <Div className={classnames("border-blue-200", bem("testimonialImage"))}>
                      <img
                        style={{ maxHeight: "100%", marginLeft: "50%", transform: "translateX(-50%)" }}
                        alt="Sam smiling"
                        src="https://21756504.fs1.hubspotusercontent-na1.net/hubfs/21756504/SI%20-%20Photography%20and%20Graphics/Testimonial%20Images/quote_3_img_d.png"
                      />
                    </Div>
                    <BodyText mb={3}>
                      "I was just released from the ER with a blood clot. The only reason they found it is because you guys advocated to make sure that my
                      ultrasound was covered. You all very literally saved my life and there is nothing I can possibly say that will express my gratitude."
                    </BodyText>
                    <BodyText style={{ marginTop: "auto" }}>Sam,</BodyText>
                    <BodyText>Customer for over 2 years</BodyText>
                  </Card>
                  <Card className="flex flex-col md:w-4/12" mb={0}>
                    <Div className={classnames("border-blue-200", bem("testimonialImage"))}>
                      <img
                        style={{ maxHeight: "100%", marginLeft: "50%", transform: "translateX(-50%)" }}
                        alt="Andrew from Express Dry Cleaning smiling"
                        src="https://21756504.fs1.hubspotusercontent-na1.net/hubfs/21756504/SI%20-%20Photography%20and%20Graphics/Testimonial%20Images/quote_2_img_d.png"
                      />
                    </Div>
                    <BodyText mb={3}>
                      "All the issues I have encountered have been handled swiftly and with professionalism. I really appreciate the support service offered to
                      all my employees. Having a professional staff to answer higher level problems is great for a growing company like mine."
                    </BodyText>
                    <BodyText style={{ marginTop: "auto" }}>Andrew,</BodyText>
                    <BodyText>Express Dry Cleaning</BodyText>
                  </Card>
                </div>
              </div>
            </div>
            <div className="bg-white" style={{ padding: "50px 0px" }}>
              <Grid className="text-center">
                <Button type="submit" color="primary" size="large" {...(employeeCountError ? { disabled: "disabled" } : {})}>
                  Get Started
                </Button>
              </Grid>
            </div>
          </form>
        )}
      </Form>
      <Footer />
      <ThemeInstaller />
    </>
  );
};

export default LandingPage;
